import React from 'react';
import { FaChalkboardTeacher, FaTimesCircle, FaUserGraduate } from 'react-icons/fa';
import styles from './styles/CalendarFilterBar.module.css';
import { CALENDAR_ROLES } from './calendarUtils';

const GROUPS = [
    { role: CALENDAR_ROLES.TEACHER, label: 'Teaching', Icon: FaChalkboardTeacher },
    { role: CALENDAR_ROLES.STUDENT, label: 'Enrolled', Icon: FaUserGraduate },
];

const byName = (a, b) => (a.name || '').localeCompare(b.name || '');

/**
 * Classroom filter chips, grouped by the role the user holds in each classroom. Selecting
 * none means showing every classroom.
 *
 * @param {Object} props
 * @param {Object[]} props.classrooms - `{ _id, name, role }` from the calendar endpoint.
 * @param {Map<string, string>} props.colorById
 * @param {string[]} props.selectedIds
 * @param {(id: string) => void} props.onToggle
 * @param {() => void} props.onClear
 * @param {boolean} [props.showClear] - also shown for a filter naming no current classroom.
 */
const CalendarFilterBar = ({ classrooms, colorById, selectedIds, onToggle, onClear, showClear }) => {
    const selected = new Set(selectedIds);
    const groups = GROUPS
        .map((group) => ({
            ...group,
            classrooms: classrooms.filter((c) => c.role === group.role).sort(byName),
        }))
        .filter((group) => group.classrooms.length > 0);

    return (
        <div className={styles.filterBar} role="group" aria-label="Filter by classroom">
            {groups.map(({ role, label, Icon, classrooms: groupClassrooms }) => (
                <div key={role} className={styles.group}>
                    <span className={styles.groupLabel}>
                        <Icon aria-hidden="true" /> {label}
                    </span>
                    <div className={styles.chips}>
                        {groupClassrooms.map((classroom) => {
                            const id = String(classroom._id);
                            const isSelected = selected.has(id);
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    className={`${styles.chip} ${isSelected ? styles.chipSelected : ''}`}
                                    style={{ '--classroom-color': colorById.get(id) }}
                                    aria-pressed={isSelected}
                                    onClick={() => onToggle(id)}
                                >
                                    <span className={styles.swatch} aria-hidden="true" />
                                    {classroom.name || 'Untitled classroom'}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}

            {(showClear ?? selectedIds.length > 0) && (
                <button type="button" className={styles.clearButton} onClick={onClear}>
                    <FaTimesCircle aria-hidden="true" /> Clear filters
                </button>
            )}
        </div>
    );
};

export default CalendarFilterBar;
